import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase";
import { useUserContext } from "../../context/UserProvider";
import PlaygroundMenu from "./PlaygroundMenu";
import LeaderboardSection from "./MemoryGameLeaderboards";
import { getLeaderboardWeekLabel, getPhotoPeriodLabel } from "./memoryGameDates";
import "./MemoryGame.css";

const REQUIRED_IMAGES = 15;
const MISMATCH_DELAY_MS = 850;
const CONFETTI_COLORS = ["#f97316", "#ef4444", "#facc15", "#22c55e", "#3b82f6", "#a855f7"];
const CONFETTI_PIECES = Array.from({ length: 28 }, (_, index) => ({
  id: index,
  color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
  left: `${(index * 37) % 100}%`,
  delay: `${(index % 9) * 0.09}s`,
  duration: `${2.2 + (index % 6) * 0.18}s`,
  drift: `${((index * 29) % 90) - 45}px`,
  rotation: `${(index * 47) % 180}deg`,
}));

function shuffle(items) {
  const shuffled = [...items];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }
  return shuffled;
}

function createDeck(images) {
  return shuffle(
    images.flatMap((image, pairIndex) => [
      { ...image, pairId: pairIndex, cardId: `${pairIndex}-a` },
      { ...image, pairId: pairIndex, cardId: `${pairIndex}-b` },
    ])
  );
}

function preloadImage(imageUrl) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = async () => {
      try {
        if (typeof image.decode === "function") await image.decode();
      } catch (decodeError) {
        // The downloaded image is still cached and safe to display if decoding is deferred.
      }
      resolve(image);
    };
    image.onerror = () => reject(new Error(`Unable to preload image: ${imageUrl}`));
    image.src = imageUrl;
  });
}

function formatElapsed(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function normalizeLeaderboards(leaderboards) {
  return {
    moves: Array.isArray(leaderboards?.moves) ? leaderboards.moves : [],
    time: Array.isArray(leaderboards?.time) ? leaderboards.time : [],
  };
}

export default function MemoryGame() {
  const { user } = useUserContext();
  const [images, setImages] = useState([]);
  const [boardData, setBoardData] = useState(null);
  const [leaderboards, setLeaderboards] = useState({ moves: [], time: [] });
  const [cards, setCards] = useState([]);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  const [matchedPairIds, setMatchedPairIds] = useState([]);
  const [moves, setMoves] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [preloadCount, setPreloadCount] = useState(0);
  const [preloadTotal, setPreloadTotal] = useState(0);
  const [error, setError] = useState("");
  const [scoreStatus, setScoreStatus] = useState("idle");
  const [scoreResult, setScoreResult] = useState(null);
  const turnTimeoutRef = useRef(null);
  const submittedRunRef = useRef("");
  const preloadedImagesRef = useRef([]);

  const gameComplete = images.length === REQUIRED_IMAGES && matchedPairIds.length === REQUIRED_IMAGES;

  const resetGame = useCallback((nextImages = images) => {
    if (turnTimeoutRef.current) clearTimeout(turnTimeoutRef.current);
    setCards(createDeck(nextImages));
    setSelectedCardIds([]);
    setMatchedPairIds([]);
    setMoves(0);
    setElapsedSeconds(0);
    setStartedAt(null);
    setLocked(false);
    setScoreStatus("idle");
    setScoreResult(null);
  }, [images]);

  useEffect(() => {
    let active = true;

    async function loadBoard() {
      try {
        const snapshot = await getDoc(doc(db, "options", "memoryGame"));
        if (!active) return;
        if (!snapshot.exists()) {
          setError("This week's memory game is being prepared. Please check back soon.");
          return;
        }

        const data = snapshot.data();
        const boardImages = (Array.isArray(data.images) ? data.images : [])
          .map((image, index) => {
            const originalImageUrl = typeof image === "string" ? image : image?.imageUrl;
            return {
              postId: image?.postId || `image-${index}`,
              imageUrl: image?.thumbnailUrl || originalImageUrl,
              originalImageUrl,
              authorName: image?.authorName || "Community member",
            };
          })
          .filter((image) => typeof image.imageUrl === "string" && image.imageUrl);

        if (boardImages.length < REQUIRED_IMAGES) {
          setError("We don't have enough community photos for a full game yet. Please check back soon.");
          return;
        }

        const playableImages = boardImages.slice(0, REQUIRED_IMAGES);
        setPreloadTotal(playableImages.length);
        setPreloadCount(0);
        const preloadedImages = await Promise.all(
          playableImages.map(async (image) => {
            const preloadedImage = await preloadImage(image.imageUrl);
            if (active) setPreloadCount((count) => count + 1);
            return preloadedImage;
          })
        );
        if (!active) return;

        preloadedImagesRef.current = preloadedImages;
        setBoardData(data);
        setLeaderboards(normalizeLeaderboards(data.leaderboards));
        setImages(playableImages);
        setCards(createDeck(playableImages));
      } catch (loadError) {
        console.error("Unable to load memory game", loadError);
        if (active) setError("We couldn't load the memory game. Please try again.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadBoard();
    return () => {
      active = false;
      preloadedImagesRef.current = [];
      if (turnTimeoutRef.current) clearTimeout(turnTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!startedAt || gameComplete) return undefined;
    const updateTimer = () => setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [startedAt, gameComplete]);

  useEffect(() => {
    if (gameComplete && startedAt) {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }
  }, [gameComplete, startedAt]);

  useEffect(() => {
    if (!gameComplete || !startedAt || !boardData?.boardId || !user?.uid) return;
    const runKey = `${boardData.boardId}:${startedAt}`;
    if (submittedRunRef.current === runKey) return;
    submittedRunRef.current = runKey;
    const finalElapsedSeconds = Math.max(1, Math.floor((Date.now() - startedAt) / 1000));

    async function submitScore() {
      setScoreStatus("saving");
      try {
        const submitMemoryGameScore = httpsCallable(getFunctions(), "submitMemoryGameScore");
        const response = await submitMemoryGameScore({
          boardId: boardData.boardId,
          moves,
          elapsedSeconds: finalElapsedSeconds,
        });
        const result = response.data || {};
        setLeaderboards(normalizeLeaderboards(result.leaderboards));
        setScoreResult(result);
        setScoreStatus("saved");
      } catch (submitError) {
        console.error("Unable to submit memory game score", submitError);
        setScoreStatus("error");
      }
    }

    submitScore();
  }, [boardData?.boardId, gameComplete, moves, startedAt, user?.uid]);

  function selectCard(card) {
    if (
      locked ||
      gameComplete ||
      selectedCardIds.includes(card.cardId) ||
      matchedPairIds.includes(card.pairId)
    ) {
      return;
    }

    if (!startedAt) setStartedAt(Date.now());

    if (selectedCardIds.length === 0) {
      setSelectedCardIds([card.cardId]);
      return;
    }

    const firstCard = cards.find((candidate) => candidate.cardId === selectedCardIds[0]);
    setSelectedCardIds([firstCard.cardId, card.cardId]);
    setMoves((currentMoves) => currentMoves + 1);

    if (firstCard.pairId === card.pairId) {
      setMatchedPairIds((currentPairs) => [...currentPairs, card.pairId]);
      setSelectedCardIds([]);
      return;
    }

    setLocked(true);
    turnTimeoutRef.current = setTimeout(() => {
      setSelectedCardIds([]);
      setLocked(false);
      turnTimeoutRef.current = null;
    }, MISMATCH_DELAY_MS);
  }

  const photoPeriodLabel = useMemo(
    () => getPhotoPeriodLabel(boardData?.periodStart, boardData?.periodEnd),
    [boardData]
  );
  const leaderboardWeekLabel = useMemo(
    () => getLeaderboardWeekLabel(boardData?.periodEnd),
    [boardData]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-blue-50 pb-8 md:pb-12">
      <PlaygroundMenu />
      <main className="max-w-4xl mx-auto px-0 py-3 sm:px-3 md:px-4 md:py-7">
        <section className="relative mx-auto overflow-hidden border-y border-slate-200 bg-white p-2 pb-4 shadow-lg sm:rounded-3xl sm:border sm:p-4 sm:pb-6 md:p-6 md:pb-8">
          {loading && <LoadingState loadedCount={preloadCount} total={preloadTotal} />}
          {!loading && error && <ErrorState message={error} />}

          {!loading && !error && (
            <>
              <div className="mb-4 grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] items-center gap-2 px-2 sm:px-3 md:mb-5 md:px-4">
                <div className="flex items-center gap-2">
                  <Stat label="Moves" value={moves} align="left" />
                </div>
                <div className="min-w-0 text-center">
                  <h1 className="mb-0.5 text-base font-extrabold leading-tight text-slate-800 sm:text-xl md:text-3xl">Flip the Feed</h1>
                  <p className="m-0 text-[8px] leading-tight text-slate-500 sm:text-[10px] md:text-xs">
                  Find all 15 pairs featuring this week's most-loved community photos.
                  </p>
                </div>
                <div className="justify-self-end">
                  <Stat label="Time" value={formatElapsed(elapsedSeconds)} align="right" />
                </div>
              </div>

              <div
                className="mx-auto grid w-full grid-cols-5 gap-1.5 sm:gap-2.5 md:max-w-[calc(120vh-14.4rem)] md:grid-cols-6 md:gap-3"
                aria-label="Memory game board"
              >
                {cards.map((card) => {
                  const faceUp = selectedCardIds.includes(card.cardId) || matchedPairIds.includes(card.pairId);
                  const matched = matchedPairIds.includes(card.pairId);
                  return (
                    <MemoryCard
                      key={card.cardId}
                      card={card}
                      faceUp={faceUp}
                      matched={matched}
                      onClick={() => selectCard(card)}
                    />
                  );
                })}
              </div>

              {gameComplete && (
                <div
                  className="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-[2px] sm:p-8"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="memory-game-complete-title"
                >
                  <Confetti />
                  <div className="relative z-10 w-full max-w-md rounded-3xl border border-white/30 bg-gradient-to-br from-orange-500 to-red-500 px-6 py-7 text-center text-white shadow-2xl sm:px-10 sm:py-9">
                    <div className="text-4xl" aria-hidden="true">🎉</div>
                    <h2 id="memory-game-complete-title" className="my-2 text-xl font-extrabold sm:text-2xl">
                      You matched every moment!
                    </h2>
                    <p className="mb-4 text-sm text-orange-50">
                      {moves} moves in {formatElapsed(elapsedSeconds)}
                    </p>
                    <ScoreSubmissionStatus status={scoreStatus} result={scoreResult} />
                    <button
                      type="button"
                      onClick={() => resetGame()}
                      className="rounded-full bg-white px-7 py-2.5 text-sm font-bold text-orange-600 shadow transition hover:scale-105"
                    >
                      Play again
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        document.getElementById("weekly-leaderboards-title")?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }}
                      className="mx-auto mt-4 block text-xs font-semibold text-white/90 underline decoration-white/50 underline-offset-4 transition hover:text-white"
                    >
                      View leaderboard
                    </button>
                  </div>
                </div>
              )}

            </>
          )}
        </section>

        {!loading && !error && (
          <p className="mb-0 mt-2 text-center text-[11px] font-medium text-slate-400 sm:text-xs">
            {photoPeriodLabel}
          </p>
        )}

        {!loading && !error && (
          <LeaderboardSection
            leaderboards={leaderboards}
            currentUserId={user?.uid}
            weekLabel={leaderboardWeekLabel}
          />
        )}
      </main>
    </div>
  );
}

function Confetti() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {CONFETTI_PIECES.map((piece) => (
        <span
          key={piece.id}
          className="flip-feed-confetti"
          style={{
            "--confetti-color": piece.color,
            "--confetti-left": piece.left,
            "--confetti-delay": piece.delay,
            "--confetti-duration": piece.duration,
            "--confetti-drift": piece.drift,
            "--confetti-rotation": piece.rotation,
          }}
        />
      ))}
    </div>
  );
}

function ScoreSubmissionStatus({ status, result }) {
  let message = "";
  if (status === "saving") message = "Saving your weekly score…";
  if (status === "error") message = "Your score could not be saved. Please try another game.";
  if (status === "saved" && result?.improvedMoves && result?.improvedTime) {
    message = "New personal best on both leaderboards!";
  } else if (status === "saved" && result?.improvedMoves) {
    message = "New personal best for fewest moves!";
  } else if (status === "saved" && result?.improvedTime) {
    message = "New personal best completion time!";
  } else if (status === "saved") {
    message = "Your previous weekly best scores still stand.";
  }

  if (!message) return null;
  return <p className="mb-4 text-xs font-semibold text-white">{message}</p>;
}

function MemoryCard({ card, faceUp, matched, onClick }) {
  return (
    <button
      type="button"
      className={`relative aspect-square overflow-hidden rounded-md border-2 p-0 shadow-sm transition-[border-color,box-shadow,transform] duration-200 sm:rounded-xl ${
        faceUp ? "border-orange-400 bg-white" : "border-blue-800 bg-blue-900 hover:-translate-y-0.5 hover:shadow-md"
      } ${matched ? "ring-2 ring-green-400 ring-offset-1" : ""}`}
      onClick={onClick}
      aria-label={faceUp ? `Photo by ${card.authorName}` : "Hidden memory card"}
      aria-pressed={faceUp}
      style={{ perspective: "700px" }}
    >
      <span
        className="absolute inset-0 block transition-transform duration-300 ease-out motion-reduce:transition-none"
        style={{
          transform: faceUp ? "rotateY(180deg)" : "rotateY(0deg)",
          transformStyle: "preserve-3d",
          willChange: "transform",
        }}
        aria-hidden="true"
      >
        <span
          className="absolute inset-0 flex items-center justify-center overflow-hidden rounded-[inherit] bg-gradient-to-br from-blue-800 via-blue-900 to-slate-950 text-lg text-white sm:text-2xl md:text-3xl"
          style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
        >
          <img
            src="/img/tbcr-logo.png"
            alt=""
            className="h-[34%] w-[34%] select-none object-contain drop-shadow-md"
            draggable="false"
            loading="eager"
          />
        </span>
        <span
          className="absolute inset-0 block overflow-hidden rounded-[inherit] bg-white"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          <img
            src={card.imageUrl}
            alt=""
            className="h-full w-full select-none object-cover"
            draggable="false"
            loading="eager"
            decoding="sync"
          />
        </span>
      </span>
    </button>
  );
}

function Stat({ label, value, align }) {
  return (
    <div className={align === "right" ? "text-right" : "text-left"}>
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 md:text-xs">{label}</span>
      <span className="font-mono text-lg font-extrabold text-slate-800 md:text-2xl">{value}</span>
    </div>
  );
}

function LoadingState({ loadedCount, total }) {
  const progress = total > 0 ? Math.round((loadedCount / total) * 100) : 0;
  return (
    <div className="flex min-h-[360px] flex-col items-center justify-center px-8 text-slate-500">
      <div className="mb-4 h-11 w-11 animate-spin rounded-full border-4 border-orange-100 border-t-orange-500" />
      <p className="mb-3 font-medium">
        {total > 0 ? `Loading community photos… ${loadedCount}/${total}` : "Preparing this week's game…"}
      </p>
      {total > 0 && (
        <div className="h-2 w-full max-w-xs overflow-hidden rounded-full bg-slate-100" aria-label={`${progress}% loaded`}>
          <div
            className="h-full rounded-full bg-gradient-to-r from-orange-400 to-orange-600 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="flex min-h-[360px] flex-col items-center justify-center px-6 text-center">
      <span className="mb-3 text-5xl" aria-hidden="true">🧩</span>
      <h2 className="mb-2 text-xl font-bold text-slate-800">Game coming soon</h2>
      <p className="m-0 max-w-md text-sm text-slate-600">{message}</p>
    </div>
  );
}
